<template>
    <main-layout>
        <div class="page-head" slot="header">
            <div>
                <div class="page-kicker">Notices &amp; forms · Rent increase</div>
                <h2>Monthly rent increase run</h2>
            </div>
            <div class="page-actions">
                <router-link class="el-button el-button--default el-button--small" to="/forms?type=rtb7">View all notices</router-link>
            </div>
        </div>

        <p class="ri-lead">On the 10th of each month, drafts are created for leases whose increase takes effect four months later.
            Nothing reaches a tenant until its property manager confirms it.</p>

        <section v-if="!isAdmin" class="ri-card">
            <b>Only administrators can see and change the monthly run.</b>
            <p class="text-muted">Your rent increase notices are in <router-link to="/forms?type=rtb7&mine=1">Rent increase notices</router-link>
                (filter "Assigned to me"). The link in your rent increase emails opens them too.</p>
        </section>
        <template v-else>
        <ol class="ri-steps" v-loading="loading">
            <li :class="nextRateOk ? 'done' : 'todo'">
                <span class="ri-step-no">Step 1 · {{ nextRateOk ? 'Done' : 'Needed' }}</span>
                <b>Confirm the yearly rate</b>
                <span>{{ nextYear }} · {{ nextRateOk ? progress.next_rate + '%' : 'not confirmed — the run will create nothing' }}</span>
            </li>
            <li>
                <span class="ri-step-no">Step 2 · Next run {{ date(progress.next_run) }}</span>
                <b>Drafts are created</b>
                <span>For notices effective {{ date(progress.next_effective) }}</span>
            </li>
            <li :class="run ? (run.confirmed >= run.drafts ? 'done' : '') : ''">
                <span class="ri-step-no">Step 3{{ run ? ' · ' + run.confirmed + ' of ' + run.drafts : '' }}</span>
                <b>Property managers confirm</b>
                <span>One by one or in bulk; or forward to a colleague</span>
            </li>
            <li :class="run ? (run.served >= run.drafts ? 'done' : '') : ''">
                <span class="ri-step-no">Step 4{{ run ? ' · ' + run.served + ' of ' + run.drafts + ' served' : '' }}</span>
                <b>Serve and record</b>
                <span>By email, or post it and record proof in the app</span>
            </li>
        </ol>

        <div class="ri-grid">
            <section class="ri-card" v-loading="settingsLoading">
                <h3>Yearly rate</h3>
                <p class="text-muted">Set by the Residential Tenancy Branch each year. A rate is used only after a person confirms it.</p>
                <el-table :data="rateRows" size="small" empty-text="No rates yet">
                    <el-table-column prop="year" label="Effective year" width="120"/>
                    <el-table-column label="Rate" width="90">
                        <template slot-scope="{row}">{{ row.rate }}%</template>
                    </el-table-column>
                    <el-table-column label="Status">
                        <template slot-scope="{row}">
                            <span v-if="row.confirmed_at">Confirmed · {{ row.confirmed_by_name }}, {{ date(row.confirmed_at) }}</span>
                            <el-button v-else size="mini" type="warning" @click="confirmRate(row.year)">Confirm {{ row.rate }}%</el-button>
                        </template>
                    </el-table-column>
                </el-table>
                <div class="ri-inline">
                    <el-input-number v-model="rateForm.year" :min="2024" :max="2100" :controls="false" size="small" class="w90" aria-label="Year"/>
                    <el-input v-model="rateForm.rate" size="small" class="w90" placeholder="Rate %" aria-label="Rate in percent"/>
                    <el-button size="small" :disabled="!rateForm.rate" @click="setRate">Save rate</el-button>
                </div>
            </section>

            <section class="ri-card" v-loading="settingsLoading">
                <h3>Who receives the drafts</h3>
                <p class="text-muted">Each draft goes to the property's rental manager in Buildium. Properties without one go to the default administrator.</p>
                <div class="ri-row">
                    <span>Default administrator</span>
                    <b>{{ settings.default_admin ? (settings.default_admin.nickname || '') + ' — ' + settings.default_admin.email : 'Not set' }}</b>
                </div>
                <div class="ri-inline">
                    <el-input v-model="adminEmailInput" size="small" placeholder="Email of a Portal user" aria-label="Default administrator email"/>
                    <el-button size="small" @click="saveAdminEmail">Save</el-button>
                </div>
                <div class="ri-row ri-hold">
                    <span>
                        <b>Office checks drafts first</b><br>
                        <span class="text-muted">On: drafts wait with the default administrator until released; managers are not emailed until then. Off: each month's drafts go straight to the property managers, who are emailed a link to confirm &amp; send. Turns itself off once every held draft is released.</span>
                    </span>
                    <el-switch v-model="settings.hold_for_review" :disabled="settingsLoading" @change="saveHold"/>
                </div>
                <p class="ri-changed" v-if="changed('hold_for_review')">{{ changed('hold_for_review') }}</p>
                <p class="ri-warn" v-if="!settings.hold_for_review && settings.held_drafts">
                    {{ settings.held_drafts }} draft(s) from an earlier run are still waiting for the office check. Turning this off does not hand them
                    over: release them in the progress section below ("Release to property managers") or from the notice list, or they never reach their property managers.
                </p>
                <div class="ri-row ri-hold">
                    <span>
                        <b>Property managers send the notices themselves</b><br>
                        <span class="text-muted">On: the manager's link says "Confirm &amp; send" — tenants are emailed and the rent entered in Buildium when they press it.
                            Off: managers only confirm; the office sends the confirmed notices from the list ("Confirmed · not served", then Confirm &amp; send).</span>
                    </span>
                    <el-switch v-model="settings.managers_send" :disabled="settingsLoading" @change="saveManagersSend"/>
                </div>
                <p class="ri-changed" v-if="changed('managers_send')">{{ changed('managers_send') }}</p>
                <h4>Sending to tenants</h4>
                <p class="text-muted">"Confirm &amp; send" emails tenants the notice with this template, then enters the new rent in Buildium.
                    Edit the wording under <router-link to="/settings/mail-templates">Settings › Email templates</router-link>.</p>
                <div class="ri-row">
                    <span>Default template</span>
                    <el-select v-model="settings.tenant_template_id" size="small" filterable placeholder="Choose a template"
                               @change="saveTemplate($event, null)">
                        <el-option v-for="t in settings.templates || []" :key="t.id" :label="t.name" :value="t.id"/>
                    </el-select>
                </div>
                <div v-for="m in managers" :key="m.id" class="ri-row">
                    <span class="text-muted">{{ m.name }}'s properties</span>
                    <el-select :value="(settings.tenant_templates || {})[m.id] || null" size="small" clearable placeholder="Default template"
                               @change="saveTemplate($event, m.id)">
                        <el-option v-for="t in settings.templates || []" :key="t.id" :label="t.name" :value="t.id"/>
                    </el-select>
                </div>
                <div class="ri-row">
                    <span>Review link in managers' emails works for</span>
                    <span><el-input-number v-model="settings.link_days" :min="7" :max="365" size="small" @change="saveLinkDays"/> days</span>
                </div>
                <h4>Emails to managers and the office</h4>
                <p class="text-muted">These emails also come from <router-link to="/settings/mail-templates">Settings › Email templates</router-link>;
                    edit the wording there. Words in braces are filled in when the email is sent ({button} is the manager's personal "Review &amp; confirm notices" button).</p>
                <div v-for="(placeholders, name) in settings.flow_templates || {}" :key="name" class="ri-row">
                    <span>{{ name }}</span>
                    <span class="text-muted">{{ placeholders.join(' ') }}</span>
                </div>
                <h4>Manufactured home parks</h4>
                <p class="text-muted">In these properties, sites rented to home owners (pad leases, and Rent in the FSJ parks) get an RTB-11a and are left out of
                    the RTB-7 run and of "Create rent increases". Homes the park rents with the site (park-owned trailers) are ordinary tenancies and stay in
                    the RTB-7 run. A park lease on any other account is left out until someone decides.</p>
                <div class="ri-accounts" v-if="settings.park_accounts">
                    <p class="text-muted">Which notice a park lease gets is decided by the account of its rent in Buildium (chart of accounts, read
                        live; the run re-reads each lease from Buildium before deciding, and sending checks again).</p>
                    <div class="ri-acc-row">
                        <span>Site lease → RTB-11a</span>
                        <el-select v-model="parkAccounts.site" multiple filterable size="small" placeholder="Rent accounts" aria-label="Site lease accounts">
                            <el-option v-for="a in settings.park_accounts.accounts" :key="a.id" :value="a.id" :label="a.name"/>
                        </el-select>
                    </div>
                    <div class="ri-acc-row">
                        <span>Park-owned home → RTB-7</span>
                        <el-select v-model="parkAccounts.home" multiple filterable size="small" placeholder="Rent accounts" aria-label="Park-owned home accounts">
                            <el-option v-for="a in settings.park_accounts.accounts" :key="a.id" :value="a.id" :label="a.name"/>
                        </el-select>
                    </div>
                    <el-button size="small" :disabled="!parkAccountsChanged" @click="saveParkAccounts">Save accounts</el-button>
                    <div class="ri-warn" v-if="settings.park_accounts.unclear.length">
                        <b>{{ settings.park_accounts.unclear.length }} park lease(s) get no notice</b>: their rent account is neither of the above.
                        Add the account above (if the chart of accounts changed) or correct the lease in Buildium.
                        <ul><li v-for="u in settings.park_accounts.unclear" :key="u.lease_id">{{ u.park }} #{{ u.unit }} — {{ u.accounts.join(', ') }}</li></ul>
                    </div>
                </div>
                <p>Each RTB-11a includes the park's change in levies and utility fees, so each park's figures must be entered and confirmed
                    before its notices can be prepared. Click a park to enter its figures.</p>
                <router-link class="el-button el-button--primary el-button--small" to="/rent-increase/parks">Enter park figures (RTB-11a)</router-link>
                <div class="ri-tags">
                    <el-tag v-for="p in settings.excluded_properties" :key="p.id" closable class="ri-park-tag"
                            @click="$router.push({path: '/rent-increase/parks', query: {park: String(p.id)}})" @close="removeExcluded(p.id)">
                        {{ p.name || ('Property ' + p.id) }}
                    </el-tag>
                    <span v-if="!settings.excluded_properties.length" class="text-muted">None</span>
                </div>
                <div class="ri-inline">
                    <el-input v-model="excludedAdd" size="small" placeholder="Buildium property ID" aria-label="Buildium property ID to exclude"/>
                    <el-button size="small" :disabled="!excludedAdd" @click="addExcluded">Add property</el-button>
                </div>
            </section>
        </div>

        <section class="ri-card" v-loading="settingsLoading">
            <h3>Signatures on notices</h3>
            <p class="text-muted">Each RTB-7 and RTB-11a is signed by the property's rental manager in Buildium, or by the default signer when the property has
                none. The two name fields are the ones on the forms. A notice without a signature cannot be sent.
                Saving updates unsent notices that are not confirmed yet.</p>
            <el-table :data="settings.signers || []" size="small" class="ri-signers">
                <el-table-column label="Signs for" min-width="160">
                    <template slot-scope="{row}">
                        <b>{{ row.key === 'default' ? 'Default signer' : row.buildium_name }}</b>
                        <div class="text-muted">{{ row.key === 'default' ? 'properties without a rental manager' : row.properties + ' propert' + (row.properties === 1 ? 'y' : 'ies') }}</div>
                    </template>
                </el-table-column>
                <el-table-column label="First and middle name(s)" min-width="170">
                    <template slot-scope="{row}">
                        <el-input size="small" v-model="signerEdits[row.key].first" :placeholder="row.default_first" aria-label="First and middle names"/>
                    </template>
                </el-table-column>
                <el-table-column label="Last name or full legal business name" min-width="230">
                    <template slot-scope="{row}">
                        <el-input size="small" v-model="signerEdits[row.key].last" :placeholder="row.default_last" aria-label="Last name or full legal business name"/>
                    </template>
                </el-table-column>
                <el-table-column label="Signature" min-width="200">
                    <template slot-scope="{row}">
                        <div class="ri-sig">
                            <img v-if="sigFor(row)" :src="sigFor(row)" alt="Signature">
                            <span v-else class="ri-sig-missing">No signature</span>
                            <label class="el-button el-button--default el-button--mini">
                                {{ sigFor(row) ? 'Replace' : 'Upload' }}
                                <input type="file" accept="image/png,image/jpeg" hidden @change="uploadSignature(row, $event)">
                            </label>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column min-width="250">
                    <template slot-scope="{row}">
                        <div class="ri-printed">
                            <div><em>Prints (B and G):</em> {{ firstOf(row) }} | {{ lastOf(row) }}</div>
                        </div>
                        <el-button size="mini" type="primary" :disabled="!signerChanged(row)" @click="saveSigner(row)">Save</el-button>
                        <div v-if="row.unsigned_drafts" class="ri-sig-missing">{{ row.unsigned_drafts }} notice(s) cannot be sent until a signature is added</div>
                    </template>
                </el-table-column>
            </el-table>
        </section>

        <section class="ri-card ri-progress" v-loading="loading">
            <div class="ri-progress-head">
                <div>
                    <h3>Confirmation by property manager</h3>
                    <el-select v-if="progress.runs.length > 1" v-model="runIndex" size="small" class="ri-run-select">
                        <el-option v-for="(r, i) in progress.runs" :key="i" :value="i"
                                   :label="'Effective ' + (r.effective_date ? date(r.effective_date) : r.effective_label || 'unknown') + ' · ' + r.drafts + ' drafts'"/>
                    </el-select>
                    <p v-else-if="run" class="text-muted">Notices effective {{ date(run.effective_date) }} · created {{ date(run.created_on) }}</p>
                </div>
                <el-button size="small" :disabled="!pendingTotal" :loading="reminding" @click="remind">Remind everyone pending</el-button>
            </div>
            <p v-if="!run && !loading" class="text-muted">No automatic drafts in the last few months.</p>
            <div v-if="run && run.held" class="ri-held">
                <span><b>{{ run.held }} draft{{ run.held === 1 ? '' : 's' }}</b> are with
                    {{ settings.default_admin ? (settings.default_admin.nickname || settings.default_admin.email) : 'the default administrator' }}
                    for checking. Property managers have not been told yet.</span>
                <router-link class="el-button el-button--default el-button--small" :to="'/forms?type=rtb7&user_id=' + (settings.default_admin ? settings.default_admin.id : '')">Check them</router-link>
                <el-button size="small" type="primary" :loading="releasing" @click="release">Release to property managers</el-button>
            </div>
            <el-table v-if="run" :data="run.people" size="small">
                <el-table-column label="Property manager" min-width="180">
                    <template slot-scope="{row}">{{ row.name }}</template>
                </el-table-column>
                <el-table-column prop="drafts" label="Drafts" width="80" align="right"/>
                <el-table-column v-if="run.held" prop="held" label="Held" width="70" align="right"/>
                <el-table-column prop="confirmed" label="Confirmed" width="100" align="right"/>
                <el-table-column prop="sent" label="Sent" width="80" align="right"/>
                <el-table-column prop="served" label="Served" width="80" align="right"/>
                <el-table-column label="Progress" min-width="160">
                    <template slot-scope="{row}">
                        <el-progress :percentage="pct(row.confirmed, row.drafts)" :stroke-width="8" :show-text="false"/>
                    </template>
                </el-table-column>
                <el-table-column width="80" align="right">
                    <template slot-scope="{row}">
                        <router-link v-if="row.user_id" :to="'/forms?type=rtb7&user_id=' + row.user_id">Open</router-link>
                    </template>
                </el-table-column>
            </el-table>
        </section>

        <router-link to="/buildium/lease-rents" class="ri-card ri-manual">
            <b>Create notices by hand</b>
            <span class="text-muted">Outside the monthly run: pick leases by rent start date or unit, enter the rate and create drafts.</span>
        </router-link>
        </template>
    </main-layout>
</template>

<script>
import ApiService from "../utils/ApiService";
import {formatDate} from "./formHelpers";
import {isAdministrator} from "../layout/navigation";

export default {
    name: "RentIncreaseRun",
    data() {
        return {
            loading: false,
            settingsLoading: false,
            reminding: false,
            releasing: false,
            progress: {next_run: '', next_effective: '', next_rate: null, runs: []},
            runIndex: 0,
            settings: {rates: {}, excluded_properties: [], default_admin: null, default_admin_email: '', hold_for_review: false, managers_send: false},
            rateForm: {year: new Date().getFullYear() + 1, rate: ''},
            adminEmailInput: '',
            signerEdits: {},
            parkAccounts: {site: [], home: []},
            excludedAdd: '',
            managers: [],
        };
    },
    computed: {
        // The settings API is for administrators; anyone else would see empty, misleading values.
        isAdmin() {
            return isAdministrator(this.$store.getters.userInfo || {});
        },
        run() {
            return this.progress.runs[this.runIndex] || null;
        },
        nextYear() {
            return this.progress.next_effective ? this.progress.next_effective.slice(0, 4) : '';
        },
        nextRateOk() {
            return this.progress.next_rate !== null && this.progress.next_rate !== undefined;
        },
        rateRows() {
            return Object.keys(this.settings.rates || {}).map(year => ({year, ...this.settings.rates[year]}));
        },
        parkAccountsChanged() {
            const pa = this.settings.park_accounts;
            if (!pa) return false;
            const same = (a, b) => a.length === b.length && a.every(x => b.includes(x));
            return !same(this.parkAccounts.site, pa.site) || !same(this.parkAccounts.home, pa.home);
        },
        pendingTotal() {
            return this.progress.runs.reduce((n, r) => n + Math.max(0, r.drafts - r.confirmed), 0);
        },
    },
    methods: {
        loadAll() {
            this.loadProgress();
            this.loadSettings();
            ApiService.get('/sterling/forms/managers').then(r => {
                this.managers = r.data || [];
            }).catch(() => {
                this.managers = [];
            });
        },
        date: formatDate,
        pct(a, b) {
            return b ? Math.round(a * 100 / b) : 0;
        },
        loadProgress() {
            this.loading = true;
            ApiService.get('/sterling/rent-increase/progress').then(r => {
                this.progress = r.data;
                this.runIndex = 0;
            }).catch(reason => {
                this.$message.error(reason.message);
            }).finally(() => {
                this.loading = false;
            });
        },
        loadSettings() {
            this.settingsLoading = true;
            ApiService.get('/sterling/rent-increase/config').then(r => {
                this.applySettings(r.data);
            }).catch(reason => {
                this.$message.error(reason.message);
            }).finally(() => {
                this.settingsLoading = false;
            });
        },
        saveParkAccounts() {
            const names = ids => ids.map(id => (this.settings.park_accounts.accounts.find(a => a.id === id) || {name: id}).name).join(', ') || 'none';
            this.$confirm(`Site leases (RTB-11a): ${names(this.parkAccounts.site)}.<br>Park-owned homes (RTB-7): ${names(this.parkAccounts.home)}.<br>`
                + 'Applies from the next run or "Prepare notices"; notices already prepared are checked again when sent.', 'Rent accounts',
                {confirmButtonText: 'Save', dangerouslyUseHTMLString: true}).then(() => {
                this.save({action: 'park_accounts', site: this.parkAccounts.site, home: this.parkAccounts.home}, 'Rent accounts saved');
            }).catch(() => {
            });
        },
        signerChanged(row) {
            const e = this.signerEdits[row.key] || {};
            const base = {first: row.first || row.default_first || '', last: row.last || row.default_last || '', sign: row.sign || ''};
            return ['first', 'last', 'sign'].some(k => (e[k] || '').trim() !== (base[k] || '').trim());
        },
        firstOf(row) {
            return (this.signerEdits[row.key].first || '').trim() || row.default_first;
        },
        lastOf(row) {
            return (this.signerEdits[row.key].last || '').trim() || row.default_last;
        },
        sigFor(row) {
            return this.signerEdits[row.key].sign || (this.signerEdits[row.key].first.trim() && this.signerEdits[row.key].first.trim() !== row.default_first ? '' : row.printed_sign);
        },
        uploadSignature(row, evt) {
            const file = evt.target.files && evt.target.files[0];
            evt.target.value = '';
            if (!file) return;
            const form = new FormData();
            form.append('file', file);
            ApiService.post('/sterling/rent-increase/signature', form, {headers: {'Content-Type': 'multipart/form-data'}}).then(r => {
                this.signerEdits[row.key].sign = r.data.url;
                this.$message.success('Signature uploaded: press Save to use it');
            }).catch(reason => {
                this.$message.error(reason.message);
            });
        },
        saveSigner(row) {
            const e = this.signerEdits[row.key];
            const who = row.key === 'default' ? 'the default signer' : row.buildium_name;
            const sig = this.sigFor(row);
            this.$confirm(`Notices signed by ${who} will print "${this.firstOf(row)}" / "${this.lastOf(row)}" with ${sig ? 'the signature shown' : 'no signature (they cannot be sent until one is added)'}.`
                + ' Unsent notices not yet confirmed are updated; confirmed and sent ones are not changed.', 'Save signer', {confirmButtonText: 'Save'}).then(() => {
                this.save({action: 'signer', key: row.key, first: e.first, last: e.last, sign: e.sign}, 'Signer saved');
            }).catch(() => {
            });
        },
        // "Last changed by Eric, Oct 20 10:29" from the server's change record.
        changed(key) {
            const c = (this.settings.last_changes || {})[key];
            if (!c) return '';
            const at = new Date(c.at);
            const when = isNaN(at) ? c.at : at.toLocaleString('en-CA', {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'});
            const to = c.to === true ? 'on' : c.to === false ? 'off' : JSON.stringify(c.to);
            return `Turned ${to} by ${c.by}, ${when}`;
        },
        applySettings(data) {
            const edits = {};
            (data.signers || []).forEach(r => {
                // The boxes show what the notices print today, as the forms have them; saving stores them.
                edits[r.key] = {first: r.first || r.default_first || '', last: r.last || r.default_last || '', sign: r.sign || ''};
            });
            this.signerEdits = edits;
            this.parkAccounts = data.park_accounts ? {site: [...data.park_accounts.site], home: [...data.park_accounts.home]} : {site: [], home: []};
            this.settings = data;
            this.adminEmailInput = data.default_admin_email || '';
        },
        save(payload, done) {
            this.settingsLoading = true;
            return ApiService.post('/sterling/rent-increase/config', payload).then(r => {
                this.applySettings(r.data);
                this.$message.success(done);
                this.loadProgress();
            }).catch(reason => {
                this.$message.error(reason.message);
            }).finally(() => {
                this.settingsLoading = false;
            });
        },
        setRate() {
            this.save({action: 'set_rate', year: this.rateForm.year, rate: this.rateForm.rate},
                `Rate for ${this.rateForm.year} saved; confirm it to use it`).then(() => {
                this.rateForm.rate = '';
            });
        },
        confirmRate(year) {
            const entry = this.settings.rates[year] || {};
            this.$confirm(`Use ${entry.rate}% for notices taking effect in ${year}?`, 'Confirm rate', {confirmButtonText: 'Confirm'}).then(() => {
                this.save({action: 'confirm_rate', year}, `Rate for ${year} confirmed`);
            }).catch(() => {
            });
        },
        saveManagersSend(on) {
            this.save({action: 'managers_send', on}, on ? 'Managers now confirm and send' : 'Managers confirm; the office sends');
        },
        saveHold(on) {
            this.save({action: 'hold_for_review', on}, on ? 'Drafts will wait for the office check' : 'Drafts go straight to the property managers');
        },
        release() {
            const n = this.run ? this.run.held : 0;
            this.$confirm(`Hand ${n} checked draft(s) to their property managers? Each manager gets an email with a personal link to confirm and send them (${this.settings.default_admin_email || 'the administrator'} gets a copy without the link). Nothing reaches tenants until a manager confirms & sends.`,
                'Release', {confirmButtonText: 'Release'}).then(() => {
                this.releasing = true;
                ApiService.post('/sterling/rent-increase/config', {action: 'release_held'}).then(r => {
                    this.$message.success(`${r.data.released} draft(s) released to ${r.data.managers} property manager(s)`
                        + (r.data.still_held ? `; ${r.data.still_held} still waiting` : ''));
                    this.loadProgress();
                    this.loadSettings();
                }).catch(reason => {
                    this.$message.error(reason.message);
                }).finally(() => {
                    this.releasing = false;
                });
            }).catch(() => {
            });
        },
        saveTemplate(templateId, managerId) {
            this.save({action: 'tenant_template', template_id: templateId || null, manager_id: managerId},
                managerId ? 'Template for this manager saved' : 'Default tenant template saved');
        },
        saveLinkDays(days) {
            if (days) this.save({action: 'link_days', days}, `Review links now work for ${days} days`);
        },
        saveAdminEmail() {
            this.save({action: 'default_admin_email', email: this.adminEmailInput}, 'Default administrator saved');
        },
        excludedIds() {
            return (this.settings.excluded_properties || []).map(p => String(p.id));
        },
        addExcluded() {
            const ids = [...new Set([...this.excludedIds(), String(this.excludedAdd).trim()])];
            this.save({action: 'excluded_properties', ids}, 'Property added').then(() => {
                this.excludedAdd = '';
            });
        },
        removeExcluded(id) {
            const p = (this.settings.excluded_properties || []).find(x => x.id === id);
            this.$confirm(`Draft notices automatically for ${p && p.name ? p.name : 'property ' + id} again?`, 'Remove', {confirmButtonText: 'Remove'}).then(() => {
                this.save({action: 'excluded_properties', ids: this.excludedIds().filter(x => x !== String(id))}, 'Property removed');
            }).catch(() => {
            });
        },
        remind() {
            this.$confirm(`Email each property manager how many notices still wait for their confirmation (${this.pendingTotal} in total)?`,
                'Remind', {confirmButtonText: 'Send reminders'}).then(() => {
                this.reminding = true;
                ApiService.post('/sterling/rent-increase/config', {action: 'remind_pending'}).then(r => {
                    this.$message.success(`Reminded ${r.data.reminded_users} person(s) about ${r.data.drafts} notice(s)`);
                }).catch(reason => {
                    this.$message.error(reason.message);
                }).finally(() => {
                    this.reminding = false;
                });
            }).catch(() => {
            });
        },
    },
    // userInfo arrives after the page mounts when it is opened directly (refresh, link): load once it says admin.
    watch: {
        isAdmin: {
            immediate: true,
            handler(admin) {
                if (admin && !this.loadedOnce) {
                    this.loadedOnce = true;
                    this.loadAll();
                }
            },
        },
    },
}
</script>

<style scoped>
.ri-lead {
    margin: 4px 6px 14px;
    color: #5B6478;
}

.ri-steps {
    list-style: none;
    padding: 0;
    margin: 0 6px 16px;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}

.ri-steps li, .ri-card {
    background: #FFFFFF;
    border: 1px solid #E3E6EE;
    border-radius: 12px;
    padding: 14px 16px;
}

.ri-steps li {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 13px;
    color: #5B6478;
}

.ri-steps li b {
    color: #1C2433;
    font-size: 15px;
}

.ri-steps li.done {
    border-color: #A7D8B4;
    background: #F3FBF5;
}

.ri-steps li.todo {
    border-color: #F4C27A;
    background: #FFF8EB;
}

.ri-step-no {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: .06em;
    text-transform: uppercase;
}

.ri-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin: 0 6px 12px;
}

.ri-card {
    margin: 0 6px 12px;
}

.ri-grid .ri-card {
    margin: 0;
}

.ri-card h3 {
    margin: 0 0 4px;
    font-size: 15px;
    font-weight: 600;
}

.ri-card h4 {
    margin: 16px 0 2px;
    font-size: 14px;
}

.ri-inline {
    display: flex;
    gap: 8px;
    margin-top: 10px;
    flex-wrap: wrap;
}

.ri-inline .el-input {
    max-width: 280px;
}

.w90 {
    width: 90px !important;
}

.ri-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 0;
    border-top: 1px solid #EEF0F5;
    border-bottom: 1px solid #EEF0F5;
    font-size: 14px;
}

.ri-hold {
    align-items: center;
    margin-top: 10px;
}

.ri-held {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    padding: 10px 12px;
    margin-bottom: 10px;
    border-radius: 10px;
    background: #EEF2FB;
    font-size: 14px;
}

.ri-held .el-button + .el-button {
    margin-left: 0;
}

.ri-signers {
    margin-top: 10px;
}

.ri-sig {
    display: flex;
    align-items: center;
    gap: 10px;
}

.ri-sig img {
    height: 34px;
    max-width: 140px;
    object-fit: contain;
    background: #FFFFFF;
    border: 1px solid #EEF0F5;
    border-radius: 4px;
}

.ri-sig label {
    margin: 0;
    cursor: pointer;
}

.ri-accounts {
    margin: 8px 0 12px;
}

.ri-acc-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 6px 0;
}

.ri-acc-row > span {
    width: 190px;
    font-size: 13px;
    font-weight: 600;
}

.ri-acc-row .el-select {
    flex: 1;
}

.ri-accounts ul {
    margin: 6px 0 0;
    padding-left: 18px;
}

.ri-printed {
    font-size: 12px;
    color: #5B6478;
    margin-bottom: 6px;
}

.ri-printed em {
    font-style: normal;
    font-weight: 600;
}

.ri-sig-missing {
    color: #B42318;
    font-size: 12px;
}

.ri-changed {
    margin: 2px 0 0;
    font-size: 12px;
    color: #8A93A6;
    text-align: right;
}

.ri-warn {
    margin: 6px 0 0;
    padding: 8px 10px;
    border-radius: 8px;
    background: #FFF4E5;
    color: #8A5A00;
    font-size: 13px;
}

.ri-park-tag {
    cursor: pointer;
}

.ri-park-tag:hover {
    text-decoration: underline;
}

.ri-tags {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 6px;
}

.ri-progress-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 8px;
}

.ri-run-select {
    margin-top: 6px;
    width: 320px;
}

.ri-manual {
    display: flex;
    flex-direction: column;
    gap: 4px;
    color: inherit;
}

.ri-manual:hover {
    border-color: #1F2D5D;
    text-decoration: none;
}

@media (max-width: 1100px) {
    .ri-steps, .ri-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 640px) {
    .ri-steps, .ri-grid {
        grid-template-columns: minmax(0, 1fr);
    }

    .ri-progress-head {
        flex-direction: column;
    }

    .ri-run-select {
        width: 100%;
    }
}
</style>
